import { useState, useCallback } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Spinner,
  Typography,
} from '@material-tailwind/react'
import { Trans, useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import { logger } from '../utils/helpers/logger'
import { useGeneralToast } from '../hooks/toast'
import { useWindowSize } from '../hooks/toolkit'
import { removeAgamiFromExperience } from '../utils/helpers/supabase.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiExperience, AgamiSpot } from '../types/supabase'

interface RemoveAgamiFromExperienceDialogProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  experienceId: string
  agamiToRemove: AgamiSpot | null
  experienceDetails: AgamiExperience | null
  refetchAgamiList: () => Promise<void>
}

export const RemoveAgamiFromExperienceDialog: React.FC<RemoveAgamiFromExperienceDialogProps> = ({
  isOpen,
  setIsOpen,
  experienceId,
  agamiToRemove,
  experienceDetails,
  refetchAgamiList,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const [deleting, setDeleting] = useState(false)
  const { width } = useWindowSize()
  const dialogSize = (width ?? 1024) <= 640 ? 'lg' : 'xs'

  const deleteFromExperience = useCallback(async () => {
    if (!experienceId || !agamiToRemove) return

    setDeleting(true)

    const { error } = await removeAgamiFromExperience({
      experienceId,
      spotId: agamiToRemove.id,
    })

    if (error) {
      logger.error(error, 'RemoveAgamiFromExperience')
      toastError('Something went wrong')
      setDeleting(false)
      return
    }

    await refetchAgamiList()
    toastSuccess('Agami removed from this experience')
    setDeleting(false)
    setIsOpen(false)
  }, [experienceId, agamiToRemove?.id, removeAgamiFromExperience, refetchAgamiList])

  return (
    <Dialog
      size={dialogSize}
      open={isOpen}
      handler={setIsOpen}
      data-testid="remove-agami-from-experience-dialog"
    >
      {/* <DialogHeader>Remove agami from experience</DialogHeader> */}
      <DialogHeader>{t('agami.deleteFromExperienceDialog.title')}</DialogHeader>
      <DialogBody>
        <Typography className="my-2">
          <Trans
            i18nKey="agami.deleteFromExperienceDialog.warning"
            values={{
              agami: agamiToRemove?.name,
              exp: experienceDetails?.name,
            }}
            components={{
              strong: <strong className="font-extrabold" />,
            }}
          />
        </Typography>
      </DialogBody>
      <DialogFooter className="justify-between gap-2">
        <Button
          size="sm"
          color={darkMode ? 'white' : ('primary' as colors)}
          variant="outlined"
          onClick={() => setIsOpen(false)}
          disabled={deleting}
        >
          {t('agami.deleteFromExperienceDialog.cancel')}
        </Button>
        <Button
          size="sm"
          color="red"
          onClick={deleteFromExperience}
          disabled={deleting}
          data-testid="remove-agami-from-experience-btn"
        >
          {deleting ? <Spinner className="h-4 w-4" /> : t('agami.deleteFromExperienceDialog.confirmRemove')}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
